import { Fragment, useState } from "react";
import { motion } from "framer-motion";

// Static sample data purely for the marketing preview below — it mirrors the
// shape of a real consolidated Profit & Loss but is not fetched from the API.
const SAMPLE_ROWS = [
  {
    name: "Income",
    amount: 128450,
    children: [
      { name: "Product Revenue", amount: 98200 },
      { name: "Service Revenue", amount: 30250 },
    ],
  },
  { name: "Cost of Goods Sold", amount: -42100 },
  { name: "Gross Profit", amount: 86350, isSubtotal: true },
  {
    name: "Operating Expenses",
    amount: -51820,
    children: [
      { name: "Payroll", amount: -34500 },
      { name: "Software & Apps", amount: -6420 },
      { name: "Professional Fees", amount: -10900 },
    ],
  },
  { name: "Net Operating Income", amount: 34530, isSubtotal: true },
];

const formatCurrency = (value) => {
  const num = Number(value);
  const formatted = Math.abs(num).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });
  return num < 0 ? `(${formatted})` : formatted;
};

function ProductPreview() {
  const [collapsed, setCollapsed] = useState({});

  const toggle = (name) =>
    setCollapsed((prev) => ({ ...prev, [name]: !prev[name] }));

  return (
    <section id="preview" className="preview-section">
      <div className="preview-inner">
        <div className="preview-copy">
          <span className="section-eyebrow">See It In Action</span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Reports that read like they were built for humans
          </motion.h2>
          <p>
            Every statement QAP generates — Balance Sheet, Profit &amp; Loss,
            Cash Flow, or Trial Balance — uses the same clean hierarchy: click
            any section to expand it, drill into a single line item across
            companies, or collapse everything down to the totals your board
            actually reads.
          </p>
          <ul className="preview-points">
            <li>Expandable, indent-aware line items for any depth of chart of accounts</li>
            <li>Consistent formatting across combined, per-entity, and month-over-month views</li>
            <li>One click to export the exact view you're looking at to Excel</li>
          </ul>
        </div>

        <motion.div
          className="preview-table-wrapper"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          viewport={{ once: true, amount: 0.2 }}
        >
          <div className="report-card preview-card">
            <div className="preview-card-header">
              <h3 className="report-title">Profit &amp; Loss — Consolidated</h3>
              <span className="preview-badge">Sample preview</span>
            </div>

            <div className="report-table-container">
              <table className="report-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {SAMPLE_ROWS.map((row) => {
                    const hasChildren = !!row.children;
                    const isCollapsed = collapsed[row.name];
                    const negative = Number(row.amount) < 0;

                    return (
                      <Fragment key={row.name}>
                        <tr
                          className={row.isSubtotal ? "total-row" : ""}
                        >
                          <td>
                            {hasChildren && (
                              <span
                                className={`expand-icon ${
                                  !isCollapsed ? "expanded" : ""
                                }`}
                                onClick={() => toggle(row.name)}
                              >
                                ▶
                              </span>
                            )}
                            <span className="row-name-text">{row.name}</span>
                          </td>
                          <td className={negative ? "negative" : "positive"}>
                            {hasChildren && !isCollapsed
                              ? ""
                              : formatCurrency(row.amount)}
                          </td>
                        </tr>

                        {hasChildren &&
                          !isCollapsed &&
                          row.children.map((child) => (
                            <tr key={child.name}>
                              <td style={{ paddingLeft: "32px" }}>
                                <span className="row-name-text">
                                  {child.name}
                                </span>
                              </td>
                              <td
                                className={
                                  Number(child.amount) < 0 ? "negative" : "positive"
                                }
                              >
                                {formatCurrency(child.amount)}
                              </td>
                            </tr>
                          ))}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default ProductPreview;
